import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #removingBundle = false; // Flag to prevent multiple updates during bundle removal

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    document.addEventListener("dialog:open", this.#handleDialogOpen);

    // Update checkout button href on initial load
    setTimeout(() => this.#updateCheckoutButtonHref(), 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    document.removeEventListener("dialog:open", this.#handleDialogOpen);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * Removes a single item from the cart.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    // Check if this is a bundle item by looking for the remove button's data attributes
    const removeButton = cartItemRowToRemove.querySelector(
      '[data-is-bundle-item="true"]',
    );

    if (removeButton) {
      // This is a bundle item - remove ALL items in the bundle including app product
      const bundleName = removeButton.getAttribute("data-bundle-name");
      const bundleClass = removeButton.getAttribute("data-bundle-class");

      try {
        // Set flag to prevent intermediate cart updates
        this.#removingBundle = true;

        // Call the removeBundleItems method to remove all bundle items
        await this.removeBundleItems(bundleName, bundleClass);

        // Clear prerenderCache
        if (typeof prerenderCache !== "undefined" && prerenderCache) {
          prerenderCache.clear();
        }

        // Update bundle selection in localStorage
        localStorage.removeItem("selectedBundle");

        // Reset flag
        this.#removingBundle = false;

        // Dispatch event to notify bundles.ts to update button states
        const bundleRemovedEvent = new CustomEvent("bundle:removed", {
          bubbles: true,
          detail: {
            bundleName: bundleName,
            bundleClass: bundleClass,
          },
        });
        document.dispatchEvent(bundleRemovedEvent);

        // Force a final cart refresh
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
      } catch (error) {
        // Reset flag on error
        this.#removingBundle = false;
        // Show error to user
        alert("Failed to remove bundle from cart. Please try again.");
      }

      return;
    }

    // Not a bundle item - remove the single item by setting its quantity to 0
    this.updateQuantity({
      line,
      quantity: 0,
      action: "change",
    });

    // Animate the row removal
    const rowHeight = cartItemRowToRemove.offsetHeight;
    cartItemRowToRemove.style.setProperty("--row-height", `${rowHeight}px`);
    cartItemRowToRemove.classList.add("removing");

    // Wait for animation to complete before proceeding
    if (!prefersReducedMotion()) {
      await onAnimationEnd(cartItemRowToRemove);
    }
  }

  /**
   * Removes a single cart item by its key
   * @param {string} itemKey - The cart item key
   */
  async removeCartItem(itemKey) {
    try {
      // First verify this item doesn't have Bundle properties
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      const itemToRemove = cart.items.find((item) => item.key === itemKey);

      if (!itemToRemove) {
        return;
      }

      // Double-check this is not a bundle item (using hidden properties)
      if (itemToRemove.properties && itemToRemove.properties._bundle) {
        return;
      }

      // Use /cart/update.js with updates parameter to target specific item by key
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          updates: {
            [itemKey]: 0,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return;
      }

      // Get updated cart and trigger update
      const updatedCartResponse = await fetch("/cart.js");
      const updatedCart = await updatedCartResponse.json();

      // Dispatch cart update event
      const cartUpdateEvent = new CustomEvent("cart:update", {
        bubbles: true,
        detail: {
          resource: updatedCart,
          sourceId: "cart-item-remove",
          data: {
            source: "cart-item-remove",
            itemCount: updatedCart.item_count,
            didError: false,
          },
        },
      });
      document.dispatchEvent(cartUpdateEvent);

      // Refresh the cart section
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } catch (error) {
      // Error handling - fail silently
    }
  }

  /**
   * Check if we're on a product, collection, or offers page with collections
   * @returns {boolean} True if we're on a page where cart should persist
   */
  #isCartPersistencePage() {
    const pathname = window.location.pathname;

    // Check if we're on a product page
    if (pathname.includes("/products/")) {
      return true;
    }

    // Check if we're on collections listing page or a specific collection page
    if (pathname.includes("/collections")) {
      return true;
    }

    // Check if we're on offers page with COLLECTIONS type offer
    if (
      pathname.includes("/pages/offers") ||
      pathname.includes("/pages/offer-app")
    ) {
      return this.#hasCollectionsOffer();
    }

    return false;
  }

  /**
   * Check if DMO data contains a COLLECTIONS type offer
   * @returns {boolean} True if there's a COLLECTIONS offer in the DMO data
   */
  #hasCollectionsOffer() {
    // Check if window.data exists and has the expected structure
    if (!window.data?.theme?.productOffer) {
      return false;
    }

    const productOffer = window.data.theme.productOffer;

    // Handle case where productOffer is an array (invalid format)
    if (Array.isArray(productOffer)) {
      return false;
    }

    // Check if offers array exists
    if (!productOffer.offers || !Array.isArray(productOffer.offers)) {
      return false;
    }

    // Check if there's a COLLECTIONS type offer
    return productOffer.offers.some((offer) => offer.type === "COLLECTIONS");
  }

  /**
   * Clears all items from the cart
   * Skips clearing on product, collection, and offers pages (with collections)
   */
  async clearCart() {
    // Don't clear cart on product, collection, and offers pages with collections
    if (this.#isCartPersistencePage()) {
      return;
    }

    try {
      const response = await fetch("/cart/clear.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to clear cart");
      }

      const clearedCart = await response.json();

      // Dispatch cart update event
      const cartUpdateEvent = new CustomEvent("cart:update", {
        bubbles: true,
        detail: {
          resource: clearedCart,
          sourceId: "cart-clear",
          data: {
            source: "cart-clear",
            itemCount: 0,
            didError: false,
          },
        },
      });
      document.dispatchEvent(cartUpdateEvent);

      // Refresh cart sections
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    } catch (error) {
      // Error handling - fail silently
    }
  }

  /**
   * Removes all items from a bundle
   * @param {string} bundleName - The name of the bundle to remove
   * @param {string} bundleClass - The class of the bundle (Standard/Better/Premium)
   */
  async removeBundleItems(bundleName, bundleClass) {
    try {
      // Get current locale from URL
      const pathname = window.location.pathname;
      const urlLocaleMatch = pathname.match(
        /^\/([a-z]{2}-[a-z]{2})(?:\/|$|\?)/,
      );
      const locale = urlLocaleMatch ? urlLocaleMatch[1] : "";
      const localePrefix = locale ? `/${locale}` : "";

      // Get current cart
      const cartResponse = await fetch(`${localePrefix}/cart.js`);
      const cart = await cartResponse.json();

      // Find all items with this bundle name AND class (using hidden properties)
      // This includes regular bundle items AND app products
      const bundleItems = cart.items.filter((item) => {
        const hasBundle =
          item.properties &&
          item.properties._bundle === bundleName &&
          item.properties._bundle_class === bundleClass;

        // Also include app products that have _app_only but match the bundle
        const isAppProduct =
          item.properties &&
          item.properties._app_only === "true" &&
          item.properties._bundle === bundleName &&
          item.properties._bundle_class === bundleClass;

        return hasBundle || isAppProduct;
      });

      if (bundleItems.length === 0) {
        // Still refresh the cart to update UI
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
        return;
      }

      // Build a single batch update to remove all bundle items at once
      /** @type {Record<string, number>} */
      const updates = {};
      bundleItems.forEach((/** @type {{key: string}} */ item) => {
        updates[item.key] = 0;
      });

      const removeResponse = await fetch(`${localePrefix}/cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });

      if (!removeResponse.ok) {
        throw new Error("Failed to remove bundle items");
      }
    } catch (error) {
      // Re-throw to allow calling code to handle
      throw error;
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    // Skip section rendering if we're in the middle of removing a bundle
    const skipRendering = this.#removingBundle;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    if (!skipRendering) {
      this.#disableCartItems();
    }

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html",
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        // Only dispatch cart update and morph if not removing bundle
        if (!skipRendering) {
          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: newCartItemCount,
              source: "cart-items-component",
              sections: parsedResponseText.sections,
            }),
          );

          morphSection(
            this.sectionId,
            parsedResponseText.sections[this.sectionId],
          );
        }
      })
      .catch((error) => {
        // Error handling - fail silently
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#updateCheckoutButtonHref();
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail?.data?.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      // Update checkout button href after morphing
      setTimeout(() => this.#updateCheckoutButtonHref(), 100);
    } else {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      // Update checkout button href after section is fully rendered
      this.#updateCheckoutButtonHref();
    }
  };

  /**
   * Handles dialog open event - updates checkout button when cart drawer opens
   */
  #handleDialogOpen = () => {
    // Small delay to ensure DOM is fully updated after dialog opens
    setTimeout(() => this.#updateCheckoutButtonHref(), 50);
  };

  /**
   * Updates the checkout button to use Storefront API checkout
   */
  #updateCheckoutButtonHref() {
    const checkoutButton = document.querySelector("[data-checkout-btn]");
    if (checkoutButton && !checkoutButton.hasAttribute("aria-disabled")) {
      // Remove href and use onclick instead
      checkoutButton.removeAttribute("href");
      checkoutButton.style.cursor = "pointer";

      // Add click handler — Storefront API path was unreliable; always use the URL-based fallback
      checkoutButton.onclick = async (/** @type {MouseEvent} */ e) => {
        e.preventDefault();
        await this.#fallbackCheckout();
      };
    }
  }

  /**
   * Fallback checkout method using URL parameters (original implementation)
   */
  async #fallbackCheckout() {
    // Get current language prefix from pathname (e.g., '/en-us')
    const pathname = window.location.pathname;
    const localeMatch = pathname.match(/^\/([a-z]{2}-[a-z]{2})/);
    const localePrefix = localeMatch ? `/${localeMatch[1]}` : "";

    // Get customer email and attributes
    const customerEmail =
      sessionStorage.getItem("customer_email") ||
      localStorage.getItem("customer_email");
    const siteParam = new URLSearchParams(window.location.search).get("site");
    // Resolve preferred distributor id (mirrors resolvePrefDistID in checkout-api.ts):
    //  - distributor_type "herbalife"/"distributor" → customer metafield distributor_id
    //    (falling back to the site id when the metafield is empty)
    //  - distributor_type "customer" (and any other/empty type, incl. guests) → the
    //    site's distributor id from GET {apiBaseUrl}/sites/{site} (window.data.distributor.id)
    // @ts-ignore - window.customerData is added by theme.liquid
    const customerData = window.customerData;
    // @ts-ignore - window.data is added by DMO module
    const dmoData = window.data;
    // A Liquid-empty metafield renders as the string "null" (theme.liquid `| default: 'null'`).
    const isRealId = (v) => typeof v === "string" && v !== "" && v !== "null";
    const distType = String(customerData?.distributor_type ?? "").trim().toLowerCase();
    const customerDistId = customerData?.distributor_id;
    const siteFallback = isRealId(dmoData?.distributor?.id) ? dmoData.distributor.id : "none";
    // Bloodwork-eligible customers always use their own distributor id (customerDistId)
    // for both distID and PrefDistID, regardless of distributor_type or whether the id
    // is populated — the site fallback is intentionally skipped for them.
    const customerTags = Array.isArray(customerData?.tags) ? customerData.tags : [];
    const isBloodworkEligible = customerTags.some(
      (t) => String(t).trim().toLowerCase() === "bloodwork_eligible"
    );
    const prefDistID = isBloodworkEligible
      ? customerDistId
      : distType === "herbalife" || distType === "distributor"
        ? (isRealId(customerDistId) ? customerDistId : siteFallback)
        : siteFallback;

    // @ts-ignore - window.data is added by DMO module
    const targetCountry = window.data?.profile?.targetCountry || "none";

    // Site profile logo: when defined, the checkout header-logo extension
    // renders it on every checkout (it reads `logo` from the `context` attribute).
    // @ts-ignore - window.data is added by DMO module
    const logoUrl = window.data?.profile?.logoUrl;

    // Site flag: when true, the checkout extension hides the Pro2col download.
    // Absent flag means enabled. Mirrors buildCheckoutAttributes() in
    // src/scripts/checkout-api.ts.
    // @ts-ignore - window.data is added by DMO module
    const pro2colDownloadDisabled = window.data?.pro2colDownloadDisabled === true;

    const context = {
      distID: prefDistID,
      ...(logoUrl ? { logo: logoUrl } : {}),
      ...(pro2colDownloadDisabled ? { pro2colDownloadDisabled: true } : {}),
    };

    const attributes = {
      context: btoa(JSON.stringify(context)),
      PrefDistID: prefDistID,
      SiteURL: siteParam || "none",
      TargetCountry: targetCountry,
      logo: logoUrl,
      pro2colDownloadDisabled: pro2colDownloadDisabled ? "true" : "false"
    };

    // Persist the context on the CART (not just the checkout URL) so it carries
    // through to the order's Additional details in Shopify admin — matching the
    // pro2col-marketing-pages behavior (Storefront cart attributes). URL params
    // alone set the checkout session only and never reach the order.
    try {
      await fetch(`${localePrefix}/cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes }),
      });
    } catch (error) {
      console.error("[checkout] Failed to persist cart attributes:", error);
    }

    // Build checkout URL with all parameters
    const params = new URLSearchParams();
    if (customerEmail) {
      params.append("checkout[email]", customerEmail);
    }

    Object.entries(attributes).forEach(([key, value]) => {
      params.append(`attributes[${key}]`, value);
    });

    const checkoutUrl = `${localePrefix}/checkout?${params.toString()}`;

    localStorage.removeItem("__ui");
    window.location.href = checkoutUrl;
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
